import ReactApexChart from "react-apexcharts";
import { useMemo, useState } from "react";
import { ApexOptions } from "apexcharts";
import { CreditHistory } from "@/api/user-credits";

export function CreditConsumptionChart({
  creditHistory,
  showApiCalls,
}: {
  creditHistory: CreditHistory;
  showApiCalls: boolean;
}) {
  const [selectedInterval] = useState<"hourly" | "daily" | "weekly">("daily");

  // Chart data (X=Date, Y=CreditsUsed, Y2=API Calls)
  const chartData = useMemo(
    () =>
      creditHistory?.daily_usage.map((day) => ({
        x: new Date(day.date).getTime(),
        creditsUsed: day.credits_used ?? 0,
        apiCalls: day.api_calls ?? 0,
      })) ?? [],
    [creditHistory],
  );

  const chartSeries = useMemo(() => {
    // Always have a "Credits Used" series
    const creditsSeries = {
      name: "Credits Used",
      data: chartData.map((pt) => [pt.x, pt.creditsUsed]),
    };

    // Conditionally add "API Calls" series if showApiCalls = true
    const apiCallsSeries = {
      name: "API Calls",
      data: chartData.map((pt) => [pt.x, pt.apiCalls]),
    };

    return showApiCalls ? [creditsSeries, apiCallsSeries] : [creditsSeries];
  }, [chartData, showApiCalls]);

  const apexOptions = useMemo(() => {
    return {
      chart: {
        type: "line",
        toolbar: { show: false },
      },
      xaxis: {
        type: "datetime",
        labels: {
          // For white axis labels:
          style: {
            colors: "#ffffff",
          },
          // Possibly different format if user picks "hourly" vs "daily" vs "weekly"
          format:
            selectedInterval === "hourly"
              ? "MMM dd, HH:mm"
              : selectedInterval === "weekly"
                ? "MMM dd"
                : "MMM dd",
        },
      },
      yaxis: {
        labels: {
          style: {
            colors: "#ffffff",
          },
          formatter: (val: number) => val.toLocaleString(),
        },
      },
      tooltip: {
        // Improved tooltip with consistent formatting
        theme: "dark", // optional: gives dark styling
        x: {
          format:
            selectedInterval === "hourly" ? "MMM dd, HH:mm" : "MMM dd, yyyy",
        },
        y: {
          formatter: (val: number) => {
            return val.toLocaleString();
          },
        },
      },
      legend: {
        show: true,
        labels: {
          colors: "#ffffff",
        },
      },
      stroke: {
        curve: "smooth",
        width: 2,
      },
      colors: ["#D1FA64", "#8884d8"],
    } satisfies ApexOptions;
  }, [selectedInterval]);

  return (
    <ReactApexChart
      type="line"
      series={chartSeries}
      options={apexOptions}
      height={400}
    />
  );
}
